import type { ECGRecord, SearchResponse, FilterOptions } from "./types";

const IS_SERVER = typeof window === "undefined";
const API_BASE = IS_SERVER ? "http://127.0.0.1:8001" : "";
const ASSETS_BASE = "";

function buildQuery(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const v of value) {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`);
      }
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
  }
  return parts.join("&");
}

export async function searchRecords(params: Record<string, unknown>): Promise<SearchResponse> {
  const query = buildQuery({ ...params, page: params.page ?? 1, page_size: params.page_size ?? 20 });
  const res = await fetch(`${API_BASE}/api/records?${query}`);
  if (!res.ok) throw new Error(`Search failed: ${res.statusText}`);
  return res.json();
}

export async function getRecord(ecgId: number): Promise<ECGRecord> {
  const res = await fetch(`${API_BASE}/api/records/${ecgId}`);
  if (!res.ok) throw new Error(`Record not found: ${res.statusText}`);
  return res.json();
}

export function getECGImageUrl(ecgId: number, format: string = "png"): string {
  return `${ASSETS_BASE}/api/records/${ecgId}/image?format=${format}`;
}

export function getOriginalImageUrl(ecgId: number): string {
  return `${ASSETS_BASE}/api/records/${ecgId}/original-image`;
}

export async function getFilterOptions(): Promise<FilterOptions> {
  const res = await fetch(`${API_BASE}/api/filters`);
  if (!res.ok) throw new Error(`Filters failed: ${res.statusText}`);
  return res.json();
}

export async function digitiseECG(
  imageFile: File,
  metadata: Record<string, unknown>
): Promise<ECGRecord> {
  const formData = new FormData();
  formData.append("image", imageFile);

  for (const [key, value] of Object.entries(metadata)) {
    if (value !== undefined && value !== null) {
      formData.append(key, String(value));
    }
  }

  const res = await fetch(`${API_BASE}/api/digitise`, {
    method: "POST",
    body: formData,
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Digitise failed: ${err}`);
  }
  return res.json();
}

export async function verifyRecord(ecgId: number): Promise<ECGRecord> {
  const res = await fetch(`${API_BASE}/api/records/${ecgId}/verify`, {
    method: "PUT",
  });
  if (!res.ok) throw new Error(`Verify failed: ${res.statusText}`);
  return res.json();
}

export async function discardRecord(ecgId: number): Promise<void> {
  const res = await fetch(`${API_BASE}/api/records/${ecgId}/discard`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(`Discard failed: ${res.statusText}`);
}

export async function getCustomDiagnosisCodes(): Promise<{ code: string; description: string }[]> {
  const res = await fetch(`${API_BASE}/api/custom-diagnosis-codes`);
  if (!res.ok) throw new Error(`Failed to get custom codes: ${res.statusText}`);
  return res.json();
}

export async function createCustomDiagnosisCode(
  code: string,
  description: string
): Promise<{ code: string; description: string }> {
  const res = await fetch(`${API_BASE}/api/custom-diagnosis-codes`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, description }),
  });
  if (!res.ok) throw new Error(`Failed to create custom code: ${res.statusText}`);
  return res.json();
}
